import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { PositionsTable } from '../components/PositionsTable';
import { Briefcase, TrendingUp, TrendingDown, BarChart3 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Positions() {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchPositions = useCallback(async () => {
    try {
      const statusParam = filter !== 'all' ? `?status=${filter}` : '';
      const res = await fetch(`${API}/api/positions${statusParam}`);
      if (res.ok) {
        const data = await res.json();
        setPositions(data.positions || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    setLoading(true);
    fetchPositions();
    const interval = setInterval(fetchPositions, 30000);
    return () => clearInterval(interval);
  }, [fetchPositions]);

  const activePositions = positions.filter(p => p.status === 'active');
  const closedPositions = positions.filter(p => p.status !== 'active');
  const totalPnl = activePositions.reduce((sum, p) => sum + (p.pnl_pct || 0), 0);
  const winRate = closedPositions.length > 0
    ? (closedPositions.filter(p => p.pnl_pct > 0).length / closedPositions.length * 100).toFixed(0)
    : 0;

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-4">
          {[1,2,3].map(i => <Skeleton key={i} className="h-24 rounded-xl bg-[hsl(var(--surface-2))]" />)}
        </div>
        <Skeleton className="h-96 w-full rounded-xl bg-[hsl(var(--surface-2))]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="positions-page">
      <div>
        <h1 className="text-2xl font-bold mb-1 flex items-center gap-2">
          <Briefcase className="w-6 h-6 text-[hsl(var(--primary))]" />
          Portfolio Positions
        </h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))]">Track your bought stocks and monitor P&L in real-time</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Briefcase className="w-4 h-4 text-[hsl(var(--primary))]" />
              <span className="text-xs text-[hsl(var(--muted-foreground))] uppercase tracking-wider font-medium">Active Positions</span>
            </div>
            <p className="font-mono text-2xl font-bold tabular-nums">{activePositions.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              {totalPnl >= 0 ? <TrendingUp className="w-4 h-4 text-emerald-400" /> : <TrendingDown className="w-4 h-4 text-red-400" />}
              <span className="text-xs text-[hsl(var(--muted-foreground))] uppercase tracking-wider font-medium">Total P&L</span>
            </div>
            <p className={`font-mono text-2xl font-bold tabular-nums ${totalPnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {totalPnl >= 0 ? '+' : ''}{totalPnl.toFixed(2)}%
            </p>
          </CardContent>
        </Card>
        <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="w-4 h-4 text-[hsl(var(--info))]" />
              <span className="text-xs text-[hsl(var(--muted-foreground))] uppercase tracking-wider font-medium">Win Rate</span>
            </div>
            <p className="font-mono text-2xl font-bold tabular-nums">{winRate}%</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter Tabs */}
      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))]">
          <TabsTrigger value="all" className="text-xs data-[state=active]:bg-[hsl(var(--accent))]">All</TabsTrigger>
          <TabsTrigger value="active" className="text-xs data-[state=active]:bg-[hsl(var(--accent))]">Active</TabsTrigger>
          <TabsTrigger value="target_hit" className="text-xs data-[state=active]:bg-[hsl(var(--accent))]">Target Hit</TabsTrigger>
          <TabsTrigger value="stop_loss" className="text-xs data-[state=active]:bg-[hsl(var(--accent))]">Stop Loss</TabsTrigger>
          <TabsTrigger value="closed" className="text-xs data-[state=active]:bg-[hsl(var(--accent))]">Closed</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Positions Table */}
      <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
        <CardContent className="p-0">
          <PositionsTable positions={positions} onRefresh={fetchPositions} />
        </CardContent>
      </Card>
    </div>
  );
}
